
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useJobs } from '../contexts/JobsContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OTPVerification from '../components/OTPVerification';
import CountryCodeSelect from '../components/CountryCodeSelect';
import PolicyAcceptance from '../components/PolicyAcceptance';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { sendOTP, formatPhoneNumber } from '@/utils/twilioService';
import { recordPolicyAcceptance, checkDuplicateEmail, checkDuplicatePhone, registerUser } from '@/utils/policyAcceptance';
import { Shield, RefreshCw, Eye, EyeOff } from 'lucide-react';

const Signup = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpTimer, setOtpTimer] = useState(60);
  const [isOTPSent, setIsOTPSent] = useState(false);
  
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(false);
  
  const [jobId, setJobId] = useState<string>('');
  const { areas, jobs, getJobsByArea } = useJobs();
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');
  const [areaJobs, setAreaJobs] = useState<any[]>([]);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobIdParam = params.get('jobId');
    
    if (jobIdParam) {
      setJobId(jobIdParam);
      const job = jobs.find(j => j.id === jobIdParam);
      if (job) {
        setSelectedAreaId(job.areaId);
        setAreaJobs(getJobsByArea(job.areaId));
      }
    }
    
    window.scrollTo(0, 0);
  }, [location.search, jobs, getJobsByArea]);

  useEffect(() => {
    if (selectedAreaId) {
      setAreaJobs(getJobsByArea(selectedAreaId));
    }
  }, [selectedAreaId, getJobsByArea]);

  const handleAreaChange = (areaId: string) => {
    setSelectedAreaId(areaId);
    setJobId('');
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (isOTPSent && otpTimer > 0) {
      intervalId = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setIsOTPSent(false);
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId);
  }, [isOTPSent, otpTimer]);

  const getFullPhoneNumber = () => {
    if (phone.startsWith('+')) {
      return phone;
    }
    return `${countryCode}${phone.replace(/^0+/, '')}`;
  };

  const handleVerifyPhone = async () => {
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    const fullPhoneNumber = getFullPhoneNumber();
    
    // Check for duplicate phone
    if (checkDuplicatePhone(fullPhoneNumber)) {
      toast.error('This phone number is already registered. Please use a different number or log in.');
      return;
    }
    
    setIsVerifying(true);
    
    try {
      const success = await sendOTP(fullPhoneNumber);
      if (success) {
        setIsOTPSent(true);
        setOtpTimer(60);
        setStep(2);
        toast.success('Verification code sent to your phone');
      } else {
        toast.error('Failed to send verification code. Please try again.');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !phone || !jobId || !password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!phoneVerified) {
      toast.error('Please verify your phone number');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (!termsAccepted || !privacyAccepted || !cookieAccepted) {
      toast.error('Please accept all policies to continue');
      return;
    }
    
    // Check for duplicate email
    if (checkDuplicateEmail(email)) {
      toast.error('This email is already registered. Please use a different email address or log in.');
      return;
    }
    
    try {
      await recordPolicyAcceptance(
        email,
        'candidate',
        termsAccepted,
        privacyAccepted,
        cookieAccepted
      );
      
      // Register user
      registerUser({
        id: crypto.randomUUID(),
        name,
        email,
        phone: getFullPhoneNumber(),
        jobId,
        areaId: selectedAreaId,
        createdAt: new Date().toISOString()
      });
      
      localStorage.setItem('authToken', 'demo-token');
      localStorage.setItem('userName', name);
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userPassword', password);
      
      toast.success('Your account has been created successfully!');
      navigate('/application');
    } catch (error) {
      console.error('Failed to create account:', error);
      toast.error('Failed to create account. Please try again.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const completeSignup = () => {
    setPhoneVerified(true);
    setStep(1);
    toast.success('Phone number verified successfully!');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16 bg-apple-gray">
        <div className="container mx-auto px-6">
          <div className="max-w-xl mx-auto bg-white rounded-xl shadow-sm p-8">
            {step === 1 ? (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-apple-text mb-2">
                    Create Your Account
                  </h1>
                  <p className="text-apple-text-secondary">
                    Sign up to apply for jobs and track your applications
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-apple-text mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-apple-text mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-apple-text mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a strong password"
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-apple-text mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        onClick={toggleConfirmPasswordVisibility}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-apple-text mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <CountryCodeSelect 
                        value={countryCode} 
                        onChange={setCountryCode}
                      />
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          setPhoneVerified(false);
                        }}
                        placeholder="Phone number"
                        className="flex-1"
                        required
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Button 
                        type="button" 
                        onClick={handleVerifyPhone}
                        disabled={isVerifying || !phone}
                        variant="outline"
                        className="w-full"
                      >
                        {isVerifying ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Sending Code...
                          </>
                        ) : phoneVerified ? (
                          <>
                            <Shield className="mr-2 h-4 w-4 text-green-500" />
                            Verified
                          </>
                        ) : (
                          <>
                            <Shield className="mr-2 h-4 w-4" />
                            Verify Phone
                          </>
                        )}
                      </Button>
                    </div>
                    {phoneVerified && (
                      <p className="text-xs text-green-600 flex items-center">
                        <Shield className="inline-block mr-1 h-3 w-3" />
                        Phone number verified
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="area" className="block text-sm font-medium text-apple-text mb-1">
                      Job Area <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={selectedAreaId}
                      onValueChange={handleAreaChange}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a job area" />
                      </SelectTrigger>
                      <SelectContent>
                        {areas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label htmlFor="job" className="block text-sm font-medium text-apple-text mb-1">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={jobId}
                      onValueChange={setJobId}
                      disabled={!selectedAreaId}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={selectedAreaId ? "Select a position" : "Select an area first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {areaJobs.map((job) => (
                          <SelectItem key={job.id} value={job.id}>
                            {job.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!selectedAreaId && (
                      <p className="text-xs text-apple-text-secondary mt-1">
                        Please select a job area first
                      </p>
                    )}
                  </div>

                  <div className="pt-2">
                    <PolicyAcceptance
                      termsChecked={termsAccepted}
                      privacyChecked={privacyAccepted}
                      cookieChecked={cookieAccepted}
                      onTermsChange={setTermsAccepted}
                      onPrivacyChange={setPrivacyAccepted}
                      onCookieChange={setCookieAccepted}
                      required
                    />
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={!phoneVerified || !termsAccepted || !privacyAccepted || !cookieAccepted}
                    >
                      Sign Up
                    </Button>
                    {!phoneVerified && (
                      <p className="text-xs text-orange-500 mt-2 text-center">
                        Please verify your phone number to continue
                      </p>
                    )}
                    {phoneVerified && (!termsAccepted || !privacyAccepted || !cookieAccepted) && (
                      <p className="text-xs text-orange-500 mt-2 text-center">
                        Please accept all policies to continue
                      </p>
                    )}
                  </div>
                  
                  <div className="text-center text-sm text-apple-text-secondary">
                    Already have an account?{" "}
                    <Link to="/login" className="font-medium text-[#5271ff] hover:underline">
                      Sign In
                    </Link>
                  </div>
                </form>
              </>
            ) : (
              <OTPVerification 
                phone={getFullPhoneNumber()}
                onVerify={completeSignup}
                onResend={handleVerifyPhone}
                remainingTime={otpTimer}
              />
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Signup;
